import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Verify token is still valid
      axios
        .get('/api/auth/profile')
        .then((response) => {
          setUser(response.data.user);
        })
        .catch(() => {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }

    // Add axios interceptor to include viewing user ID in requests
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const viewingAsUserId = sessionStorage.getItem('viewingAsUserId');
        if (viewingAsUserId) {
          config.headers['X-Viewing-As-User-Id'] = viewingAsUserId;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add axios interceptor to handle authentication errors globally
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        // Check if response is HTML instead of JSON (server misconfiguration)
        if (typeof response.data === 'string' && response.data.includes('<!doctype')) {
          console.warn('API returned HTML instead of JSON - possible routing issue');
          return Promise.reject(new Error('API returned HTML instead of JSON'));
        }
        return response;
      },
      (error) => {
        // Silently handle HTML response errors (when server returns React app instead of JSON)
        if (error.message && error.message.includes('Unexpected token')) {
          // Don't spam console for JSON parse errors
          return Promise.reject(error);
        }

        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          const readOnlyError =
            typeof error.response.data?.error === 'string' &&
            error.response.data.error.toLowerCase().includes('read-only');
          if (readOnlyError) {
            return Promise.reject(error);
          }
          // Only logout if we have a token (avoid logout loops)
          if (localStorage.getItem('token')) {
            localStorage.removeItem('token');
            delete axios.defaults.headers.common['Authorization'];
            setUser(null);
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptors on unmount
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/auth/login', {
        username,
        password,
      });

      const { user, token } = response.data;

      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed',
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post('/api/auth/register', userData);

      const { user, token } = response.data;

      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed',
      };
    }
  };

  const acceptInvite = async (token, password) => {
    try {
      const response = await axios.post(
        '/api/auth/accept-invite',
        { token, password },
        { headers: { 'X-Activation-Submit': '1' } } // CSRF defence: custom header forces CORS preflight on cross-origin abuse
      );

      const { user, token: jwt } = response.data;

      localStorage.setItem('token', jwt);
      axios.defaults.headers.common['Authorization'] = `Bearer ${jwt}`;
      setUser(user);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to accept invitation',
        error_code: error.response?.data?.error_code || null,
      };
    }
  };

  const logout = async () => {
    try {
      // Call backend logout endpoint to trigger session summary email
      await axios.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with logout even if API call fails
    }

    // Clear local auth state
    localStorage.removeItem('token');
    sessionStorage.removeItem('viewingAsUserId');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const isDataReadOnly = Boolean(user?.dataCompleteAt && user?.role !== 'admin');

  const value = {
    user,
    login,
    register,
    acceptInvite,
    logout,
    updateUser,
    isDataReadOnly,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthContext;
